import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Search, Trash2, X } from 'lucide-react';
import { archiveChatSession, listChatSessions, patchChatSession } from 'shared/services/chatService';
import { useAppDialog } from 'shared/context/AppDialogContext';
import BrandLoader from 'shared/components/ui/BrandLoader';
import {
  activityNeutralButtonClass,
  activitySearchInputClass,
  activitySearchWrapperClass,
  dataTableFocusRing,
} from 'features/activity/styles/dataTableStyles';
import {
  formatSessionAge,
  groupSessionsByRecency,
  SESSION_GROUP_LABEL_CLASS,
} from 'features/dashboard/chatSessions';

const PAGE_SIZE = 25;
const UNTITLED = 'Untitled chat';

const iconButtonClass =
  `inline-flex h-6 w-6 items-center justify-center rounded-md text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)] ${dataTableFocusRing}`;

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * Lucid's conversation history: a header, a search field, then the
 * conversations grouped by when they were last used. One list, worn by the
 * panel's popover and the chat page's history column alike.
 *
 * Keyboard: the search field has focus on open; ArrowDown moves into the
 * rows, arrows move between them, Enter opens one, Escape closes.
 *
 * Props:
 *   teamId            number
 *   activeSessionId   the conversation on screen, marked in the list
 *   onSelect(id)      open a conversation
 *   onNewChat()       optional; shows "New chat" in the header
 *   onClose()         optional; Escape, and the close button when showClose
 *   onDeleted(id)     optional; after a conversation is deleted
 *   initialSessions   optional; shown while the first page loads
 *   showClose         render a close button in the header
 */
export default function ChatHistoryList({
  teamId,
  activeSessionId = null,
  onSelect,
  onNewChat,
  onClose,
  onDeleted,
  initialSessions,
  showClose = false,
}) {
  const dialog = useAppDialog();
  const [sessions, setSessions] = useState(() =>
    Array.isArray(initialSessions) ? initialSessions.slice(0, PAGE_SIZE) : [],
  );
  const [loaded, setLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const loadFirstPage = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setErr('');
    try {
      const data = await listChatSessions(teamId, { limit: PAGE_SIZE, offset: 0 });
      const list = Array.isArray(data?.sessions) ? data.sessions : Array.isArray(data) ? data : [];
      setSessions(list);
      setHasMore(Boolean(data?.pagination?.hasMore));
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Could not load chats.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [teamId]);

  useEffect(() => {
    loadFirstPage();
    searchRef.current?.focus();
  }, [loadFirstPage]);

  // A conversation started while the list is open does not exist on the
  // server until its first message lands; refetch once the one on screen is
  // one the list has not seen. `sessions` is deliberately absent: including it
  // would re-run on every load and, while the server catches up, loop.
  useEffect(() => {
    if (!activeSessionId || !loaded) return;
    if (!sessions.some((s) => sameId(s.id ?? s._id, activeSessionId))) loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId, loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    setErr('');
    try {
      const data = await listChatSessions(teamId, { limit: PAGE_SIZE, offset: sessions.length });
      const list = Array.isArray(data?.sessions) ? data.sessions : [];
      const seen = new Set(sessions.map((s) => String(s.id)));
      setSessions([...sessions, ...list.filter((s) => !seen.has(String(s.id)))]);
      setHasMore(Boolean(data?.pagination?.hasMore));
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Could not load more chats.');
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, sessions, teamId]);

  const onScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasMore || loadingMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) loadMore();
  }, [hasMore, loadingMore, loadMore]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return sessions;
    return sessions.filter((s) => (s.title || UNTITLED).toLowerCase().includes(needle));
  }, [sessions, q]);

  const groups = useMemo(() => groupSessionsByRecency(filtered), [filtered]);

  const pick = useCallback((id) => {
    onSelect?.(id);
    onClose?.();
  }, [onSelect, onClose]);

  const remove = useCallback(async (s) => {
    const ok = await dialog.confirm(`“${s.title || UNTITLED}” will be removed from your history.`, {
      title: 'Delete chat?',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await archiveChatSession(s.id, teamId);
      setSessions((prev) => prev.filter((x) => !sameId(x.id, s.id)));
      onDeleted?.(s.id);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Could not delete the chat.');
    }
  }, [dialog, teamId, onDeleted]);

  const rename = useCallback(async (s) => {
    const next = await dialog.prompt('', {
      title: 'Rename chat',
      defaultValue: s.title || '',
      placeholder: UNTITLED,
      confirmLabel: 'Rename',
    });
    const title = typeof next === 'string' ? next.trim() : '';
    if (!title || title === s.title) return;
    try {
      const data = await patchChatSession(s.id, teamId, { title });
      const saved = data?.session?.title || title;
      setSessions((prev) => prev.map((x) => (sameId(x.id, s.id) ? { ...x, title: saved } : x)));
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Could not rename the chat.');
    }
  }, [dialog, teamId]);

  const rows = () => Array.from(listRef.current?.querySelectorAll('[data-history-row]') || []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (onClose) { e.stopPropagation(); onClose(); }
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const all = rows();
    if (!all.length) return;
    e.preventDefault();
    const at = all.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      all[at < 0 ? 0 : Math.min(at + 1, all.length - 1)].focus();
    } else if (at <= 0) {
      searchRef.current?.focus();
    } else {
      all[at - 1].focus();
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter' && q.trim() && filtered.length) {
      e.preventDefault();
      pick(filtered[0].id);
    }
  };

  const showLoader = loading && sessions.length === 0;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      onKeyDown={onKeyDown}
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      <div className="flex shrink-0 items-center gap-2 px-3 pb-2 pt-3">
        <h2 className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[color:var(--text-primary)]">History</h2>
        {onNewChat && (
          <button
            type="button"
            onClick={() => { onNewChat(); onClose?.(); }}
            className={`${activityNeutralButtonClass} px-3`}
          >
            New chat
          </button>
        )}
        {showClose && onClose && (
          <button type="button" onClick={onClose} aria-label="Close history" title="Close" className={`${iconButtonClass} h-8 w-8`}>
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        )}
      </div>

      <div className="shrink-0 border-b border-[color:var(--border-default)] px-3 pb-3">
        <label className={`${activitySearchWrapperClass} w-full`}>
          <Search size={13} className="shrink-0 text-[color:var(--text-muted)]" aria-hidden />
          <input
            ref={searchRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Search chats"
            aria-label="Search chats"
            className={activitySearchInputClass}
          />
          {q && (
            <button
              type="button"
              onClick={() => { setQ(''); searchRef.current?.focus(); }}
              aria-label="Clear search"
              className="shrink-0 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
            >
              <X size={12} strokeWidth={2} aria-hidden />
            </button>
          )}
        </label>
      </div>

      <div ref={listRef} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-1.5 pb-2 scrollbar-thin">
        {showLoader && (
          <div className="flex justify-center py-8">
            <BrandLoader size="sm" label="Loading chats" />
          </div>
        )}

        {err && (
          <p className="px-2 pt-3 text-[12px] text-[color:var(--feedback-error)]" role="alert">{err}</p>
        )}

        {!showLoader && !err && loaded && sessions.length === 0 && (
          <p className="px-2 py-8 text-center text-[12px] text-[color:var(--text-muted)]">No chats yet.</p>
        )}

        {sessions.length > 0 && filtered.length === 0 && (
          <p className="px-2 py-8 text-center text-[12px] text-[color:var(--text-muted)]">
            No chats match “{q.trim()}”.
          </p>
        )}

        {groups.map(({ label, items }) => (
          <section key={label} aria-label={label}>
            <h3 className={SESSION_GROUP_LABEL_CLASS}>{label}</h3>
            <ul>
              {items.map((s) => {
                const active = sameId(activeSessionId, s.id);
                const title = s.title || UNTITLED;
                return (
                  <li key={s.id} className="group relative">
                    <button
                      type="button"
                      data-history-row
                      onClick={() => pick(s.id)}
                      aria-current={active ? 'true' : undefined}
                      title={title}
                      className={`flex h-8 w-full items-center gap-2 rounded-md px-2 text-left group-focus-within:pr-14 group-hover:pr-14 hover:bg-surface-hover focus-visible:bg-surface-hover ${dataTableFocusRing} ${
                        active ? 'bg-surface-hover font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]'
                      }`}
                    >
                      <span className="min-w-0 flex-1 truncate text-[13px]">{title}</span>
                      <span className="shrink-0 text-[12px] font-normal tabular-nums text-[color:var(--text-muted)] group-focus-within:invisible group-hover:invisible">
                        {formatSessionAge(s.updatedAt || s.updated_at || s.createdAt || s.created_at)}
                      </span>
                    </button>
                    <div className="absolute right-1 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 group-focus-within:flex group-hover:flex">
                      <button type="button" onClick={() => rename(s)} aria-label={`Rename ${title}`} title="Rename" className={iconButtonClass}>
                        <Pencil size={12} strokeWidth={2} aria-hidden />
                      </button>
                      <button type="button" onClick={() => remove(s)} aria-label={`Delete ${title}`} title="Delete" className={iconButtonClass}>
                        <Trash2 size={12} strokeWidth={2} aria-hidden />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        {hasMore && !q.trim() && (
          <div className="flex justify-center pt-2">
            <button type="button" disabled={loadingMore} onClick={loadMore} className={activityNeutralButtonClass}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
